import { Injectable } from '@angular/core';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { CollectionPaths } from '@models/collections';
import { UserMatterViewHistory } from '@models/userViewHistory';
import { Action, State, StateContext } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import {
	DEFAULT_STATE_BASE,
	FirestoreStateBaseStateModel,
} from '@state/FirestoreStateBase';
import {
	BusinessPathParams,
	FirestoreStateBaseForBusinessWithSubscription,
} from '@state/FirestoreStateBaseForBusiness';
import { MatterActions } from '@state/matter';
import { orderBy } from 'lodash';
import * as actions from './recently-viewed.actions';

export interface RecentlyViewedStateModel
	extends FirestoreStateBaseStateModel<RecentlyViewedPathParams> {
	id?: string;
	items: UserMatterViewHistory[];
	currentMatterId?: string;
}

interface RecentlyViewedPathParams extends BusinessPathParams {
	userId: string;
}
type Ctx = StateContext<RecentlyViewedStateModel>;

const DEFAULT_STATE: RecentlyViewedStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
	currentMatterId: null,
};

@State<RecentlyViewedStateModel>({
	name: 'recentlyviewed',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class RecentlyViewedState extends FirestoreStateBaseForBusinessWithSubscription<
	UserMatterViewHistory,
	RecentlyViewedStateModel
> {
	constructor(private snackbarService: SnackbarService) {
		super(
			(pathParams) =>
				CollectionPaths.business_user_matterViewHistory(
					pathParams.businessId,
					this.authUser?.uid,
				),
			(doc) => ({
				items: doc as UserMatterViewHistory[],
			}),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.Create)
	async create(ctx: Ctx, { payload }: actions.Create) {
		const state = ctx.getState();
		const currentFile = state.items.find((x) => x.id === payload.id);

		if (currentFile) {
			if (!currentFile.added) {
				payload.added = currentFile.lastViewed;
			}

			if (currentFile.hideFromTabs) {
				payload.hideFromTabs = false;
				payload.added = new Date().getTime();
			}
		} else {
			payload.added = new Date().getTime();
		}

		await this.repo.CreateOrMerge(payload.id, payload);

		/**
		 * Prevent having JS do this sorting, do it in firestore
		 * Update firestore index to sort by lastViewed and LIMIT by 10 Utilize queryFn in state
		 * @see {@link RecentlyViewedState#getQueryConstraints}
		 */
		const itemsToRemoveFrom = state.items?.filter((x) => !x.pinned) ?? [];
		const ordered = orderBy(itemsToRemoveFrom, ['lastViewed'], ['desc']);
		const deleteItems = ordered.splice(10);
		deleteItems.forEach((x: UserMatterViewHistory) => this.repo.Delete(x.id));
	}

	@Action(MatterActions.Get)
	updateCurrentMatterViewedHistory(
		{ patchState }: Ctx,
		{ payload }: MatterActions.Get,
	): void {
		patchState({
			currentMatterId: payload,
		});
	}

	@Action(actions.UpdateViewedDocs)
	updateViewDocs(ctx: Ctx, { payload }: actions.UpdateViewedDocs) {
		return this.repo.OverwriteFields(
			payload.matterId,
			`viewedDocuments.${payload.viewedDocId}`,
			new Date().getTime(),
			['id', payload.matterId],
		);
	}

	@Action(actions.Pin)
	pin(ctx: Ctx, { payload }: actions.Pin) {
		this.repo.OverwriteFields(payload.matterId, 'pinned', payload.pin);
		this.snackbarService.openSnackBar({
			text: payload.pin ? 'File pinned.' : 'File unpinned.',
			icon: ICON.PIN,
			iconStyle: payload.pin ? ICON_STYLE.SOLID : ICON_STYLE.REGULAR,
		});
	}

	@Action(actions.HideFromTabs)
	hideFromTabs(ctx: Ctx, { payload }: actions.HideFromTabs) {
		return this.repo.OverwriteFields(payload.matterId, 'hideFromTabs', true);
	}
}
